import { Auth } from 'aws-amplify';
import { Loader } from 'components';
import { useInfoPopup } from 'hooks';
import { setSentryUser } from 'lib/sentry';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearHeaders } from 'services';
import { changeProfileLanguage } from 'services/user';
import { setAnalyticsUserId } from 'utils/analytics';

import {
  MessageContainer,
  VerifyingLoaderSlot,
  VerifyingState,
} from '../styled';
import { CognitoError, getAmplifyAuthErrorMessage } from '../utils';

const VerifyPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showInfoPopup } = useInfoPopup();

  const onFailure = (error: CognitoError) => {
    const title = getAmplifyAuthErrorMessage(t, error);

    showInfoPopup({
      type: 'error',
      title: title === t('auth.userNotFound') ? t('auth.codeExpired') : title,
    });

    navigate('/signup');
  };

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const code = search.get('c');
    const userName = search.get('u');
    const role = search.get('role');
    const firstName = search.get('fName');
    const lastName = search.get('lName');
    const communityId = search.get('communityId');
    const invitationId = search.get('invitationId');
    const invitationCode = search.get('invitationCode');
    const groups = search.get('groups');

    if (!code || !userName) {
      showInfoPopup({
        type: 'error',
        title: t('errors.invalidRequest'),
      });
      navigate('/');
    }

    (async () => {
      await Auth.signOut();
      setAnalyticsUserId(null);
      setSentryUser(null);
      clearHeaders();

      Auth.confirmSignUp(userName, code, {
        clientMetadata: {
          firstName,
          lastName,
          role,
          communityId,
          groups,
          language: i18n.language,
        },
      })
        .then(() => {
          changeProfileLanguage(userName, i18n.language);

          showInfoPopup({
            type: 'success',
            title: t('auth.confirmed'),
          });

          if (invitationId) {
            if (invitationCode) {
              navigate(
                `/community/${invitationId}?invitationCode=${invitationCode}`,
              );
            } else {
              navigate(`/communities/${invitationId}/landing`);
            }

            return;
          }

          navigate('/');
        })
        .catch((err) => {
          onFailure(err);
        });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <VerifyingState>
      <VerifyingLoaderSlot>
        <Loader size="56px" type={null} />
      </VerifyingLoaderSlot>
      <MessageContainer>{t('auth.verifying')}</MessageContainer>
    </VerifyingState>
  );
};

export default VerifyPage;
